/**
 * useGlobalAudio - 全局音频管理器
 *
 * 确保同一时间只播放一个音频，解决多组件同时播放问题
 *
 * 设计：
 * - 单例模式：所有组件共享同一个 Audio 实例
 * - 自动停止：播放新音频时自动停止之前的
 * - 状态同步：使用 zustand 同步播放状态到所有组件
 * - 纯 CDN 音频：不使用 Web Speech API fallback
 */

import { create } from 'zustand';

// ============================================
// Global Audio State (Singleton)
// ============================================

interface GlobalAudioState {
  /** 是否正在播放 */
  isPlaying: boolean;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 当前播放的 URL */
  currentUrl: string | null;
  /** 错误信息 */
  error: Error | null;
  /** 内部方法 */
  _setPlaying: (playing: boolean) => void;
  _setLoading: (loading: boolean) => void;
  _setCurrentUrl: (url: string | null) => void;
  _setError: (error: Error | null) => void;
}

export const useGlobalAudioStore = create<GlobalAudioState>((set) => ({
  isPlaying: false,
  isLoading: false,
  currentUrl: null,
  error: null,
  _setPlaying: (playing) => set({ isPlaying: playing }),
  _setLoading: (loading) => set({ isLoading: loading }),
  _setCurrentUrl: (url) => set({ currentUrl: url }),
  _setError: (error) => set({ error }),
}));

// ============================================
// Global Audio Instance (Singleton)
// ============================================

let globalAudio: HTMLAudioElement | null = null;

/**
 * 获取全局 Audio 实例
 */
function getGlobalAudio(): HTMLAudioElement {
  if (!globalAudio) {
    globalAudio = new Audio();

    // 播放结束
    globalAudio.onended = () => {
      useGlobalAudioStore.getState()._setPlaying(false);
      useGlobalAudioStore.getState()._setCurrentUrl(null);
    };

    // 播放错误
    globalAudio.onerror = () => {
      useGlobalAudioStore.getState()._setPlaying(false);
      useGlobalAudioStore.getState()._setLoading(false);
      useGlobalAudioStore
        .getState()
        ._setError(new Error(globalAudio?.error?.message || 'Audio playback failed'));
    };

    // 可以播放
    globalAudio.oncanplaythrough = () => {
      useGlobalAudioStore.getState()._setLoading(false);
    };
  }
  return globalAudio;
}

// ============================================
// Global Audio Functions
// ============================================

/**
 * 停止所有播放
 */
export function stopGlobalAudio(): void {
  // 停止 Audio
  if (globalAudio) {
    globalAudio.pause();
    globalAudio.currentTime = 0;
  }

  // 更新状态
  useGlobalAudioStore.getState()._setPlaying(false);
  useGlobalAudioStore.getState()._setLoading(false);
  useGlobalAudioStore.getState()._setCurrentUrl(null);
}

/**
 * 播放音频 URL
 *
 * 如果 URL 为空，静默跳过（不播放任何声音）
 */
export async function playGlobalAudio(url: string | null | undefined): Promise<void> {
  if (!url) {
    // 无 URL 时静默，不播放任何声音
    return;
  }

  const state = useGlobalAudioStore.getState();

  // 如果正在播放同一个 URL，停止
  if (state.currentUrl === url && state.isPlaying) {
    stopGlobalAudio();
    return;
  }

  // 停止之前的播放
  stopGlobalAudio();

  const audio = getGlobalAudio();

  state._setLoading(true);
  state._setError(null);
  state._setCurrentUrl(url);

  try {
    audio.src = url;
    audio.load();
    await audio.play();
    state._setPlaying(true);
    state._setLoading(false);
  } catch (error) {
    console.error('[GlobalAudio] Play failed:', error);
    state._setLoading(false);
    state._setPlaying(false);
    state._setError(error instanceof Error ? error : new Error(String(error)));
  }
}

// ============================================
// Hook
// ============================================

export interface UseGlobalAudioReturn {
  /** 播放音频 URL */
  play: (url: string | null | undefined) => Promise<void>;
  /** 停止所有播放 */
  stop: () => void;
  /** 是否正在播放 */
  isPlaying: boolean;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 当前播放的 URL */
  currentUrl: string | null;
  /** 错误信息 */
  error: Error | null;
}

/**
 * 全局音频 Hook
 *
 * @example
 * ```tsx
 * const { play, stop, isPlaying } = useGlobalAudio();
 *
 * // 播放音频
 * play(audioUrl);
 *
 * // 停止
 * stop();
 * ```
 */
export function useGlobalAudio(): UseGlobalAudioReturn {
  const { isPlaying, isLoading, currentUrl, error } = useGlobalAudioStore();

  return {
    play: playGlobalAudio,
    stop: stopGlobalAudio,
    isPlaying,
    isLoading,
    currentUrl,
    error,
  };
}
